/**
 * Tiebreak logic — §7.6.
 *
 * Winner determination:
 * 1. Most total points wins.
 * 2. If tied on total, most nature tokens wins.
 * 3. If still tied, all tied players share victory (co-winners).
 *
 * Pure — no I/O, no mutations.
 */

export interface PlayerScore {
  readonly playerId: string;
  readonly total: number;
  readonly natureTokens: number;
}

/**
 * Returns the array of winner player IDs.
 * Length 1 for a clear winner, length > 1 for co-winners.
 */
export const computeWinners = (
  playerScores: readonly PlayerScore[],
): readonly string[] => {
  if (playerScores.length === 0) {
    return [];
  }

  const maxTotal = Math.max(...playerScores.map((p) => p.total));
  const topByTotal = playerScores.filter((p) => p.total === maxTotal);

  if (topByTotal.length === 1) {
    return [topByTotal[0]!.playerId];
  }

  // Tiebreak: most nature tokens
  const maxNature = Math.max(...topByTotal.map((p) => p.natureTokens));
  const topByNature = topByTotal.filter((p) => p.natureTokens === maxNature);

  return topByNature.map((p) => p.playerId);
};
